import { jsxs, jsx } from "react/jsx-runtime";
import { Actions, Action } from "../../ai-elements/actions.js";
import { Shimmer } from "../../ai-elements/shimmer.js";
import brandclub_colors from "../../colors/brandclubColors.js";
import { CopyIcon } from "../../icons/CopyIcon.js";
import { ShareIcon } from "../../icons/ShareIcon.js";
import { ThumbsDownIcon } from "../../icons/ThumbsDownIcon.js";
import { ThumbsUpIcon } from "../../icons/ThumbsUpIcon.js";
import React__default, { useState, useMemo, useRef, useLayoutEffect } from "react";
import { toast } from "sonner";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { sendMessageFeedback, setConversationSharing } from "../api.js";
import { renderMessageContentWithCarousels } from "../utils/messageRenderer.js";
import { ProductSearchQueriesSection } from "./ProductSearchQueriesSection.js";
import DevInfoPanel from "./DevInfoPanel.js";
import { StoreLocatorWidget } from "./StoreLocatorWidget/index.js";
import { OrderCardList } from "./OrderCard.js";
import { LoginWallWidget, ChainOfThoughtWidget, MessageBubbleWidget, themeColor, customColor, FollowUpQueriesWidget } from "@brandclub/bc-widgets";
const ChatMessageComponent = React__default.forwardRef(
  ({
    role,
    content,
    bottomRef,
    isTyping = false,
    isStreaming = false,
    isLastMessage = false,
    messageIndex,
    conversationId,
    feedback,
    followUpQueries = [],
    nextUserMessage,
    productMetadataMap,
    surveyMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries = [],
    previewQueryProducts = [],
    featuredProduct,
    streamingFeaturedProduct,
    storeLocator,
    streamingStoreLocator,
    orders,
    devInfo,
    loginRequired,
    streamingLoginRequired,
    onSignIn,
    imageUrl,
    imageData,
    imageMimeType,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView
  }) => {
    var _a, _b;
    const isUser = role === "user";
    const [liked, setLiked] = useState((feedback == null ? void 0 : feedback.type) === "like");
    const [disliked, setDisliked] = useState((feedback == null ? void 0 : feedback.type) === "dislike");
    const [localSelectedFollowUp, setLocalSelectedFollowUp] = useState(void 0);
    const derivedSelectedFollowUp = useMemo(() => {
      if (!nextUserMessage || !followUpQueries.length) return void 0;
      return followUpQueries.find((query) => query === nextUserMessage);
    }, [nextUserMessage, followUpQueries]);
    const selectedFollowUp = derivedSelectedFollowUp || localSelectedFollowUp;
    const userMessageRef = useRef(null);
    const [isMultiline, setIsMultiline] = useState(false);
    useLayoutEffect(() => {
      if (!isUser || !userMessageRef.current) return;
      const el = userMessageRef.current;
      const textNode = el.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const rects = range.getClientRects();
        setIsMultiline(rects.length > 1);
      } else {
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
        setIsMultiline(el.scrollHeight > lineHeight * 1.5);
      }
    }, [isUser, content]);
    const formatProductForCopy = (product, isFeatured) => {
      const parts = [`Title: ${product.title}`];
      if (product.brandName) {
        parts.push(`Brand: ${product.brandName}`);
      }
      if (product.retailPrice !== void 0) {
        parts.push(`Price: $${product.retailPrice.toFixed(2)}`);
      }
      const url = `${window.location.origin}/product/p/${product.stacklineSku}/product`;
      parts.push(`URL: ${url}`);
      if (isFeatured && product.featureBullets && product.featureBullets.length > 0) {
        parts.push(`
Features:`);
        product.featureBullets.forEach((bullet) => {
          parts.push(`• ${bullet}`);
        });
      }
      return parts.join("\n");
    };
    const convertProductTagsToText = (text) => {
      let result = text;
      result = result.replace(
        /<product\s+featured="true"\s+stacklineSku="([^"]+)"\s*(?:question="[^"]+")?\s*\/?>/g,
        (match, stacklineSku) => {
          let product = productMetadataMap.get(stacklineSku);
          if (!product) {
            product = (featuredProduct == null ? void 0 : featuredProduct.product) || (streamingFeaturedProduct == null ? void 0 : streamingFeaturedProduct.product);
            if (product && product.stacklineSku !== stacklineSku) {
              product = void 0;
            }
          }
          if (product) {
            return "\n\n" + formatProductForCopy(product, true) + "\n\n";
          }
          return match;
        }
      );
      result = result.replace(/<product_group[^>]*>([\s\S]*?)<\/product_group>/g, (match, innerContent) => {
        const productTags = innerContent.match(/<product\s+stacklineSku="([^"]+)"\s*\/?>/g);
        if (!productTags) return match;
        const products = [];
        productTags.forEach((tag) => {
          const skuMatch = tag.match(/stacklineSku="([^"]+)"/);
          if (skuMatch) {
            const sku = skuMatch[1];
            const product = productMetadataMap.get(sku);
            if (product) {
              products.push(formatProductForCopy(product, false));
            }
          }
        });
        return products.length > 0 ? "\n\n" + products.join("\n---\n") + "\n\n" : match;
      });
      result = result.replace(/<citation\s+[^>]*>([\s\S]*?)<\/citation>/g, (match, citationText) => {
        const sourceMatch = match.match(/source\s*=\s*"([^"]+)"/);
        const source = sourceMatch ? sourceMatch[1] : "";
        if (source) {
          return `[${citationText.trim()}](${source})`;
        }
        return citationText;
      });
      return result;
    };
    const handleCopy = async () => {
      try {
        const textWithProducts = convertProductTagsToText(content);
        await navigator.clipboard.writeText(textWithProducts);
        toast.success("Message copied to clipboard");
      } catch (error) {
        toast.error("Failed to copy message");
      }
    };
    const handleShare = async () => {
      if (!conversationId) {
        console.warn("Cannot share: missing conversationId");
        return;
      }
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await setConversationSharing({
          conversationId,
          enabled: true,
          messageIndex,
          headersToInject: headers,
          apiBaseUrl
        });
        await navigator.clipboard.writeText(result.shareUrl);
        toast.success("Share link copied to clipboard");
      } catch (error) {
        console.error("Failed to share conversation:", error);
        toast.error("Failed to share conversation");
      }
    };
    const handleLike = async () => {
      var _a2;
      if (!conversationId || messageIndex === void 0) {
        toast.error("Cannot like: conversation not loaded");
        return;
      }
      try {
        const newState = !liked;
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "like" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        setLiked(newState);
        setDisliked(false);
        toast.success(newState ? "Message liked" : "Like removed");
        if (onUpdateFeedback && ((_a2 = result.message.metadata) == null ? void 0 : _a2.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        toast.error("Failed to save feedback");
      }
    };
    const handleDislike = async () => {
      var _a2;
      if (!conversationId || messageIndex === void 0) {
        toast.error("Cannot dislike: conversation not loaded");
        return;
      }
      try {
        const newState = !disliked;
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "dislike" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        setDisliked(newState);
        setLiked(false);
        toast.success(newState ? "Message disliked" : "Dislike removed");
        if (onUpdateFeedback && ((_a2 = result.message.metadata) == null ? void 0 : _a2.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        toast.error("Failed to save feedback");
      }
    };
    const handleFollowUpClick = (query) => {
      if (!isLastMessage || selectedFollowUp) return;
      setLocalSelectedFollowUp(query);
      onSearch == null ? void 0 : onSearch(query);
    };
    const getJustifyContent = (alignment) => {
      switch (alignment) {
        case "right":
          return "flex-end";
        case "center":
          return "center";
        default:
          return "flex-start";
      }
    };
    const renderUserMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-user-message",
        displayName: "User Message Bubble",
        initialState: {
          bgColor: customColor(brandclub_colors.blueWhite),
          textColor: themeColor("textPrimary"),
          maxWidth: "70%",
          alignment: "right"
        },
        children: (styles, resolveColor) => {
          const borderRadius = isMultiline ? styles.borderRadius : styles.borderRadiusShort;
          const fontFamily = styles.fontFamilyCss ? styles.fontFamilyCss !== "inherit" ? styles.fontFamilyCss : "var(--concierge-user-message-font-family, inherit)" : "var(--concierge-user-message-font-family, inherit)";
          return /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: getJustifyContent(styles.alignment),
                width: "100%"
              },
              children: /* @__PURE__ */ jsx("div", { style: { maxWidth: styles.maxWidth }, children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "message-content user-message",
                  style: {
                    maxWidth: "100%",
                    minWidth: "50px",
                    backgroundColor: resolveColor(styles.bgColor),
                    color: resolveColor(styles.textColor),
                    borderRadius: `${borderRadius}px`,
                    wordWrap: "break-word",
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    fontSize: `${styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    fontFamily,
                    lineHeight: styles.lineHeight
                  },
                  children: /* @__PURE__ */ jsx("div", { ref: userMessageRef, style: { whiteSpace: "pre-wrap" }, children: content })
                }
              ) })
            }
          );
        }
      }
    );
    const renderAssistantMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-assistant-message",
        displayName: "Assistant Message Bubble",
        initialState: {
          bgColor: themeColor("secondary"),
          textColor: themeColor("textPrimary"),
          maxWidth: "100%",
          paddingX: 0,
          paddingY: 0,
          alignment: "left"
        },
        children: (styles, resolveColor) => /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: getJustifyContent(styles.alignment),
              width: "100%"
            },
            children: /* @__PURE__ */ jsxs("div", { style: { maxWidth: styles.maxWidth }, children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "message-content",
                  style: {
                    maxWidth: "100%",
                    minWidth: "50px",
                    backgroundColor: resolveColor(styles.bgColor),
                    color: resolveColor(styles.textColor),
                    borderRadius: `${styles.borderRadius}px`,
                    wordWrap: "break-word",
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    fontSize: `${styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    lineHeight: styles.lineHeight
                  },
                  children: [
                    renderMessageContentWithCarousels(
                      content,
                      productMetadataMap,
                      streamingProductGroups,
                      isStreaming,
                      isStreaming,
                      // streaming messages are already processed
                      onVariantSelect,
                      surveyMetadataMap
                    ),
                    isStreaming && content.length > 0 && /* @__PURE__ */ jsx(
                      "span",
                      {
                        style: {
                          display: "inline-block",
                          width: "2px",
                          height: "1em",
                          backgroundColor: "currentColor",
                          animation: "blink 1s infinite",
                          marginLeft: "2px"
                        }
                      }
                    ),
                    orders && orders.length > 0 && /* @__PURE__ */ jsx(OrderCardList, { orders })
                  ]
                }
              ),
              bottomRef && /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    height: "1px",
                    width: "100%"
                  },
                  ref: bottomRef
                }
              ),
              !isTyping && !isStreaming && content.length > 0 ? /* @__PURE__ */ jsxs(Actions, { className: "tw:mt-2", children: [
                /* @__PURE__ */ jsx(Action, { label: "Copy", tooltip: "Copy", onClick: handleCopy, children: /* @__PURE__ */ jsx(CopyIcon, {}) }),
                /* @__PURE__ */ jsx(Action, { label: "Like", tooltip: "Like", onClick: handleLike, children: /* @__PURE__ */ jsx(ThumbsUpIcon, { className: liked ? "tw:fill-current tw:text-primary" : "" }) }),
                /* @__PURE__ */ jsx(Action, { label: "Dislike", tooltip: "Dislike", onClick: handleDislike, children: /* @__PURE__ */ jsx(ThumbsDownIcon, { className: disliked ? "tw:fill-current tw:text-primary" : "" }) }),
                !isSharedView && /* @__PURE__ */ jsx(Action, { label: "Share", tooltip: "Share", onClick: handleShare, children: /* @__PURE__ */ jsx(ShareIcon, {}) })
              ] }) : null,
              !isTyping && !isStreaming && followUpQueries.length > 0 && !(nextUserMessage && !selectedFollowUp) && /* @__PURE__ */ jsx(FollowUpQueriesWidget, { widgetId: "concierge-follow-up-queries", displayName: "Follow-up Suggestions", children: (widgetStyles, resolveColor2) => {
                const isDisabled = !isLastMessage || !!selectedFollowUp;
                return /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      display: "flex",
                      flexWrap: "wrap",
                      gap: `${widgetStyles.containerGap}px`,
                      marginTop: "16px",
                      width: "100%"
                    },
                    children: followUpQueries.map((query, index) => {
                      const isSelected = selectedFollowUp === query;
                      let bgColor = resolveColor2(widgetStyles.pillBgColor);
                      let textColor = resolveColor2(widgetStyles.pillTextColor);
                      let borderColor = resolveColor2(widgetStyles.pillBorderColor);
                      let cursor = "pointer";
                      let opacity = 1;
                      if (isSelected) {
                        bgColor = resolveColor2(widgetStyles.pillBgColorSelected);
                        textColor = resolveColor2(widgetStyles.pillTextColorSelected);
                        borderColor = resolveColor2(widgetStyles.pillBorderColorSelected);
                        cursor = "default";
                      } else if (isDisabled) {
                        opacity = 0.5;
                        cursor = "not-allowed";
                      }
                      return /* @__PURE__ */ jsx(
                        "button",
                        {
                          onClick: () => handleFollowUpClick(query),
                          disabled: isDisabled && !isSelected,
                          style: {
                            padding: `${widgetStyles.pillPaddingY}px ${widgetStyles.pillPaddingX}px`,
                            borderRadius: `${widgetStyles.pillBorderRadius}px`,
                            border: `${widgetStyles.pillBorderWidth}px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            color: textColor,
                            fontSize: `${widgetStyles.pillFontSize}px`,
                            fontWeight: widgetStyles.pillFontWeight,
                            cursor,
                            opacity,
                            transition: "all 0.2s ease",
                            lineHeight: "22px",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          },
                          onMouseEnter: (e) => {
                            if (!isDisabled && !isSelected) {
                              e.currentTarget.style.backgroundColor = resolveColor2(widgetStyles.pillBgColorHover);
                            }
                          },
                          onMouseLeave: (e) => {
                            if (!isDisabled && !isSelected) {
                              e.currentTarget.style.backgroundColor = resolveColor2(widgetStyles.pillBgColor);
                            }
                          },
                          title: query,
                          children: query
                        },
                        index
                      );
                    })
                  }
                );
              } })
            ] })
          }
        )
      }
    );
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: `message ${role}`,
        style: {
          display: "flex",
          flexDirection: "column",
          width: "100%"
        },
        children: [
          !isUser && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0) && /* @__PURE__ */ jsx(
            ProductSearchQueriesSection,
            {
              previewSearchQueries,
              previewQueryProducts,
              isStreaming,
              currentStatus,
              hasContent: content.length > 0,
              isLastMessage
            }
          ),
          !isUser && devInfo && /* @__PURE__ */ jsx(DevInfoPanel, { devInfo }),
          !isUser && (storeLocator || streamingStoreLocator) && /* @__PURE__ */ jsx(
            StoreLocatorWidget,
            {
              stacklineSku: ((_a = storeLocator || streamingStoreLocator) == null ? void 0 : _a.stacklineSku) || "",
              apiUrl: apiBaseUrl
            }
          ),
          !isUser && (loginRequired || streamingLoginRequired) && /* @__PURE__ */ jsx(
            LoginWallWidget,
            {
              widgetId: "concierge-login-wall",
              title: ((_b = loginRequired || streamingLoginRequired) == null ? void 0 : _b.message) || "Sign in required",
              description: "Sign in to access your order history and personalized features",
              buttonText: "Sign In",
              onSignIn
            }
          ),
          !isUser && isTyping && /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Chain of Thought", children: (styles, resolveColor) => styles.thinkingEnabled ? /* @__PURE__ */ jsx("div", { style: { maxWidth: "100%" }, children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "message-content",
              style: {
                maxWidth: "100%",
                backgroundColor: "none",
                color: resolveColor(styles.thinkingTextColor),
                borderRadius: `${styles.thinkingBorderRadius}px`,
                borderBottomLeftRadius: `${styles.thinkingBorderRadiusBottomLeft}px`,
                borderBottomRightRadius: `${styles.thinkingBorderRadius}px`,
                wordWrap: "break-word",
                padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px`
              },
              children: /* @__PURE__ */ jsx(
                Shimmer,
                {
                  as: "p",
                  style: { fontSize: `${styles.thinkingTextFontSize}px` },
                  duration: styles.shimmerDuration,
                  children: styles.thinkingText
                }
              )
            }
          ) }) : null }),
          isUser && (imageUrl || imageData) && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                marginBottom: "0.5rem"
              },
              children: /* @__PURE__ */ jsx("div", { style: { maxWidth: "70%" }, children: /* @__PURE__ */ jsx(
                "img",
                {
                  src: imageUrl || `data:${imageMimeType};base64,${imageData}`,
                  alt: "User uploaded image",
                  style: {
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "cover",
                    display: "block",
                    borderRadius: "12px"
                  }
                }
              ) })
            }
          ),
          !isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble())
        ]
      }
    );
  }
);
const ChatMessage = React__default.memo(ChatMessageComponent, (prevProps, nextProps) => {
  var _a, _b, _c, _d, _e, _f;
  return prevProps.role === nextProps.role && prevProps.content === nextProps.content && prevProps.isTyping === nextProps.isTyping && prevProps.bottomRef === nextProps.bottomRef && prevProps.isStreaming === nextProps.isStreaming && prevProps.isLastMessage === nextProps.isLastMessage && prevProps.currentStatus === nextProps.currentStatus && prevProps.isWaitingForFollowUpQueries === nextProps.isWaitingForFollowUpQueries && ((_a = prevProps.previewSearchQueries) == null ? void 0 : _a.length) === ((_b = nextProps.previewSearchQueries) == null ? void 0 : _b.length) && ((_c = prevProps.previewQueryProducts) == null ? void 0 : _c.length) === ((_d = nextProps.previewQueryProducts) == null ? void 0 : _d.length) && ((_e = prevProps.followUpQueries) == null ? void 0 : _e.length) === ((_f = nextProps.followUpQueries) == null ? void 0 : _f.length) && prevProps.nextUserMessage === nextProps.nextUserMessage && prevProps.productMetadataMap === nextProps.productMetadataMap && prevProps.streamingProductGroups === nextProps.streamingProductGroups && prevProps.featuredProduct === nextProps.featuredProduct && prevProps.streamingFeaturedProduct === nextProps.streamingFeaturedProduct && prevProps.storeLocator === nextProps.storeLocator && prevProps.streamingStoreLocator === nextProps.streamingStoreLocator && prevProps.orders === nextProps.orders && prevProps.loginRequired === nextProps.loginRequired && prevProps.streamingLoginRequired === nextProps.streamingLoginRequired && prevProps.onSignIn === nextProps.onSignIn && prevProps.imageUrl === nextProps.imageUrl && prevProps.imageData === nextProps.imageData && prevProps.imageMimeType === nextProps.imageMimeType && prevProps.onSearch === nextProps.onSearch && prevProps.onVariantSelect === nextProps.onVariantSelect && prevProps.onSuggestionClick === nextProps.onSuggestionClick;
});
export {
  ChatMessage
};
//# sourceMappingURL=ChatMessage.js.map
